import React, { PropsWithChildren, useCallback, useEffect, useState } from 'react';
import WebApp from '@twa-dev/sdk';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Typography } from '../../ui-kit';
import styles from './tg-provider.module.scss';
import { useAuth } from '@/store/useAuth';
import { debounce } from 'lodash';
import { profilesApi } from '@/api';
import { IDevice, UAParser } from 'ua-parser-js';
import { routesApp } from '@/constans/routes';
import { callYaMetrika } from '@/utils/call-ya-metrika';
import { useToast } from '@/store/useToast';
import { ErrorNotificationIcon } from '@/assets/icons';
import { useNavHistory } from '@/store/useNavHistory';
import { useActionCableCreate } from '@/hooks/use-action-cable';
import { jwtService } from '@/services/jwt.service';
import { SocketPayload } from '@/types/socket-payload';

export const TgProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const navigate = useNavigate();
    const showToast = useToast((state) => state.show);

    const handleBack = useCallback(() => navigate(-1), [navigate]);

    const userData = useAuth((state) => state.getUser());
    const setUserData = useAuth((state) => state.setProfile);

    useEffect(() => {
        /*
         * show always back button
         * if (WebApp.BackButton.isVisible) {
         *     WebApp.onEvent('backButtonClicked', handleBack);
         * }
         */
        WebApp.onEvent('backButtonClicked', handleBack);
        return () => {
            WebApp.offEvent('backButtonClicked', handleBack);
        };
    }, [handleBack]);

    useEffect(() => {
        const isActive = userData?.active;
        if (
            isActive !== null &&
            isActive !== undefined
        )
            setOverlayOpened(isActive === false);
    }, [userData]);

    const CHANEL_NAME = 'ProfileChannel';
    const isDev = process.env.NODE_ENV === 'development';
    let chanel: any;
        const getPathFromWebSocketUrlIsDev = (url: string) => {
        if (isDev) {
            // const parsedUrl = new URL(url);
            // return parsedUrl.pathname + parsedUrl.search + parsedUrl.hash;
            // TODO: url to env
            return 'http://localhost:8255/cable';
            // return 'http://localhost:3300/cable';
        }
        return url;
    };
    const cable = useActionCableCreate(
        getPathFromWebSocketUrlIsDev('/cable')
    );

    useEffect(() => {
        const parser = new UAParser();
        setDevice(parser.getDevice());
        chanel = cable.subscriptions.create(
            {
                channel: CHANEL_NAME,
                authentication: process.env.NODE_ENV === 'development' ? jwtService.getToken() : WebApp.initData,
            },
            {
                connected() {
                    console.log('conented');
                },
                received(payload: SocketPayload) {
                    console.log('received', payload);
                    window.dispatchEvent(
                        new CustomEvent('profileChannelReceived', { detail: payload })
                    )
                },
                disconnected() {
                    console.log('WebSocket отключён. Пытаемся переподключиться...');
                    console.log('unsubscribe from ProfileChannel')
                },
                rejected() {
                    console.log('Сервер отклонил подписку!');
                },
            },
        );
    }, []);



    const location = useLocation();

    const [isOverlayOpened, setOverlayOpened] = useState<boolean | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [device, setDevice] = useState<IDevice | null>(null);

    const PATH_SEPARATOR = '/';

    const splitPathToChunks = (path: string) => path.split(PATH_SEPARATOR);

    const joinPathChunks = (chunks: string[]) => chunks.join(PATH_SEPARATOR);

    useEffect(() => {
        const chunksCount = splitPathToChunks(location.pathname).filter((e: string) => e.length).length;
        if (chunksCount >= 2) WebApp.BackButton.show();
        else WebApp.BackButton.hide();

        // override default stack
        const PATH_PARAM_PREFIX = ':';
        const chunks = location.pathname.split(PATH_SEPARATOR);

        if (window.history.length === 1) {
            if (
                location
                    .pathname
                    .startsWith(
                        joinPathChunks(
                            splitPathToChunks(
                                routesApp.chat
                            )
                                .filter(
                                    (e) => !e.startsWith(PATH_PARAM_PREFIX)
                                )
                        )
                    )
            ) {
                navigate(routesApp.messages, {
                    replace: true,
                });
                navigate(joinPathChunks(chunks));
            }
        }
    }, [location]);

    const activateProfileDebounce = debounce(async () => {
        if (isLoading) return;
        setIsLoading(true);
        callYaMetrika('return_to_search');
        try {
            const res = await profilesApi.activate();
            if (res.status !== 200) return showActivateError();
            showToast({
                icon:
                    <svg
                        width="20px"
                        height="19.61px"
                        viewBox="0 0 28 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M14 0.666992C5.67626 0.666992 0.299589 9.26409 0.202344 9.42064C0.0714498 9.58552 0.000141514 9.78981 0 10.0003C0.000165576 10.1809 0.052724 10.3576 0.151302 10.5089C0.152511 10.5107 0.153726 10.5126 0.154948 10.5144C0.170995 10.5475 4.68424 19.3337 14 19.3337C23.2762 19.3337 27.7828 10.6338 27.8378 10.5271C27.8415 10.5211 27.8451 10.515 27.8487 10.5089C27.9473 10.3576 27.9998 10.1809 28 10.0003C28 9.79065 27.9293 9.58709 27.7995 9.42246C27.7989 9.42185 27.7983 9.42124 27.7977 9.42064C27.7004 9.26409 22.3237 0.666992 14 0.666992ZM14 3.46699C17.6083 3.46699 20.5333 6.39206 20.5333 10.0003C20.5333 13.6086 17.6083 16.5337 14 16.5337C10.3917 16.5337 7.46667 13.6086 7.46667 10.0003C7.46667 6.39206 10.3917 3.46699 14 3.46699ZM14 7.20033C13.2574 7.20033 12.5452 7.49532 12.0201 8.02043C11.495 8.54553 11.2 9.25772 11.2 10.0003C11.2 10.7429 11.495 11.4551 12.0201 11.9802C12.5452 12.5053 13.2574 12.8003 14 12.8003C14.7426 12.8003 15.4548 12.5053 15.9799 11.9802C16.505 11.4551 16.8 10.7429 16.8 10.0003C16.8 9.25772 16.505 8.54553 15.9799 8.02043C15.4548 7.49532 14.7426 7.20033 14 7.20033Z"
                            fill="#fff" />
                    </svg>,
                msg: 'Вы снова в поиске!'
            });
            setOverlayOpened(false);
            setUserData({
                ...userData,
                active: true,
            });
        } catch (e) {
            showActivateError();
        } finally {
            setIsLoading(false);
        }
    }, 750);

    const showActivateError = () =>
        showToast({
            icon: <ErrorNotificationIcon />,
            msg: 'Ошибка. Попробуйте ещё раз'
        });

    const onActivateProfileClick = () => activateProfileDebounce();

    const { setPrevRoute } =
        useNavHistory(state => state);

    useEffect(() => {
        // Save current location as "previous" before updating
        return () =>
            setPrevRoute(location.pathname);
    }, [location.pathname]);

    return (
        <div>
            <Outlet />
            {location.pathname === 'profile' || !isOverlayOpened ? (
                // placeholder
                <div />
            ) : (
                <div
                    className={[
                        styles.overlay,
                        ...(device?.type === 'mobile' || device?.type === 'tablet' ? [styles.overlayMobile] : []),
                    ].join(' ')}
                >
                    <Typography
                        center
                        fontWeight="semibold"
                        fontFamily="SFProText"
                    >
                        Профиль скрыт,{' '}
                        <a
                            href="#"
                            onClick={onActivateProfileClick}
                        >
                            вернуться в поиск
                        </a>
                        ?
                    </Typography>
                </div>
            )}
        </div>
    );
};
